import * as THREE from 'three';
import { hexDist, hexToWorld, type BattleState, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import type { AscuasSky } from './ascuas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { clamp01, lum, repaint } from './halls.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * What comes up (Las vías 4, docs/SAGAS.md): the clearing re-dressed as the bastion's own forge yard at dawn. In the
 * middle, where the node would be, the forge: a brick hearth on a stone plinth with its coals glowing in the mouth, a
 * hood and a tall chimney, the anvil on its stump, the bellows, the quench trough, a rack of tongs. Soot-dark
 * flagstones, cinder on the roads, the bastion's wall round the edge, and on the side away from the roads the fast way
 * coming up out of a stone mouth, its rails running in toward the forge. Warm braziers on the wall.
 *
 * The fight's own marks: a tower Bruni marks gets a red ring of runes on the ground round it that pulses faster as its
 * time runs out (kept: a cold flash; turned: a red flare). The boss's weak side, under his right arm (the sim's
 * `risingWeak`: facing − a quarter turn), shows a molten seam that drips embers.
 *
 * Draw calls: the yard (one merged mesh), the hot parts (coals, chimney mouth; one mesh, its colour breathing), the
 * braziers' fire, a small pool of mark rings, a glow layer. No lights.
 */

type Rules = NonNullable<NonNullable<BattleState['rules']>['rising']>;

const STONE = ['#34333a', '#3c3a40', '#2f2e34', '#38363c'];
const CINDER = ['#2a2523', '#302a27', '#252120'];
const GROUT = '#18161a';
const WALL = '#5c5a5f';
const WALL_DARK = '#3d3b40';
const BRICK = ['#6a3e30', '#5a3428', '#743f2e'];
const BRICK_DARK = '#3a2620';
const IRON = '#2f3137';
const IRON_EDGE = '#5a5e68';
const WOOD = '#4a372b';
const PLANK = '#5a4130';
const LEATHER = '#3e2a20';
const WATER = '#14212c';
const FIRE = new THREE.Color('#ffb070');
const FIRE_CORE = new THREE.Color('#fff0d8');
const COAL = new THREE.Color('#ff6a2a');
const COAL_HOT = new THREE.Color('#ffc890');
const RED = new THREE.Color('#ff4f3a');
const RED_LIGHT = new THREE.Color('#ff8a6e');
const SEAM = new THREE.Color('#ff7a2a');
const SEAM_CORE = new THREE.Color('#ffe2b8');
/** How many towers can be marked at once (more than the sim ever marks). */
const POOL = 4;

export class ForgeLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  private hotMat: THREE.MeshBasicMaterial;
  private rings: { outer: THREE.Mesh; inner: THREE.Mesh; mat: THREE.MeshBasicMaterial; runes: THREE.MeshBasicMaterial }[] = [];
  private braziers: THREE.Vector3[] = [];
  /** The hearth's mouth and the chimney's top (for its glow, sparks and smoke). */
  private mouth = new THREE.Vector3();
  private chimney = new THREE.Vector3();
  /** Where the fast way comes up (for Dorn's automatons' dust). */
  private railMouth = new THREE.Vector3();
  private now = 0;
  private hit = 0;
  private last = 0;
  private v = new THREE.Vector3();
  private c = new THREE.Color();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    private r: Rules,
  ) {
    this.group.name = 'forge-yard';
    const kit = new Kit();
    const hot = new Kit();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const slab = new THREE.CylinderGeometry(0.62, 0.64, 0.05, 6);
    const rock = new THREE.DodecahedronGeometry(0.14, 0);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    const R = map.bastion;
    const onPath = new Set(map.sectors.flatMap((sec) => sec.path.map((h) => `${h.q},${h.r}`)));
    const g0 = ctx.ground(0, 0);

    // The floor: soot-dark flagstones; the roads cinder; the middle hex under the forge's plinth.
    let n = 0;
    for (let q = -R; q <= R; q++)
      for (let rr = -R; rr <= R; rr++) {
        const d = hexDist({ q, r: rr }, { q: 0, r: 0 });
        if (d > R || d === 0) continue;
        const c = hexToWorld(q, rr);
        const p = ctx.at(c.x, c.y, 0);
        const road = onPath.has(`${q},${rr}`);
        n++;
        kit.add(slab, (road ? CINDER[n % CINDER.length] : STONE[(n * 7 + q * 3) % STONE.length]) as string, place(p.x, ctx.ground(p.x, p.z) + 0.005, p.z, ((n * 13) % 5 - 2) * 0.008));
        if (!road && n % 3 === 0) kit.add(box, GROUT, place(p.x, ctx.ground(p.x, p.z) + 0.032, p.z, (n % 6) * 0.52, new THREE.Vector3(0.012, 0.006, 0.9)));
      }

    this.buildForge(kit, hot, box, cyl, g0);

    // The fast way: on the side away from the roads, a stone mouth at the edge, its rails running in toward the forge.
    const gates = map.sectors.map((sec) => {
      const c = hexToWorld(sec.gate.q, sec.gate.r);
      return Math.atan2(c.y, c.x);
    });
    const off = (a: number): number => Math.min(...gates.map((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g)))));
    let best = 0;
    for (let i = 0; i < 36; i++) {
      const a = (i / 36) * Math.PI * 2;
      if (off(a) > off(best)) best = a;
    }
    const ER = (R + 0.75) * WORLD_PER_HEX;
    this.buildRails(kit, hot, box, best, ER);

    // Round the edge: the bastion's wall in runs, clear of the roads' mouths and the fast way, braziers on it.
    const near = (a: number, list: number[], w: number): boolean => list.some((g) => Math.abs(Math.atan2(Math.sin(a - g), Math.cos(a - g))) < w);
    const runs = 22;
    for (let i = 0; i < runs; i++) {
      const a = (i / runs) * Math.PI * 2;
      if (near(a, gates, 0.3) || near(a, [best], 0.3)) continue;
      const p = new THREE.Vector3(Math.cos(a) * ER, 0, Math.sin(a) * ER);
      p.y = ctx.ground(p.x, p.z);
      const len = (2 * Math.PI * ER) / runs + 0.02;
      this.wallRun(kit, box, p, a, len, i);
      if (i % 4 === 1) this.brazier(kit, hot, box, p, a, i);
    }
    // Yard clutter between the wall and the towers: coal heaps, crates, barrels, an ingot stack.
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2 + 0.35;
      if (near(a, gates, 0.35) || near(a, [best], 0.35)) continue;
      const rr = ER - 0.55;
      const x = Math.cos(a) * rr;
      const z = Math.sin(a) * rr;
      const y = ctx.ground(x, z);
      if (i % 3 === 0) for (let k = 0; k < 5; k++) kit.add(rock, ['#151417', '#1d1b1f', '#121114'][k % 3] as string, place(x + Math.cos(k * 2.1) * 0.12, y + 0.06 + (k === 4 ? 0.08 : 0), z + Math.sin(k * 2.1) * 0.12, k, 1.1 + (k % 2) * 0.4));
      else if (i % 3 === 1) {
        kit.add(box, PLANK, place(x, y + 0.13, z, a, 0.26));
        kit.add(box, WOOD, place(x + 0.05, y + 0.36, z + 0.03, a + 0.3, 0.2));
        kit.add(box, IRON, place(x, y + 0.26, z, a, new THREE.Vector3(0.27, 0.02, 0.27)));
      } else {
        kit.add(cyl, PLANK, place(x, y + 0.17, z, 0, new THREE.Vector3(0.36, 0.34, 0.36)));
        for (const h of [0.06, 0.28]) kit.add(cyl, IRON, place(x, y + h, z, 0, new THREE.Vector3(0.38, 0.03, 0.38)));
      }
    }

    const yardMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.1, flatShading: true });
    const yard = kit.build(yardMat, 'forge-yard');
    this.mats.push(yardMat);
    if (yard) {
      yard.receiveShadow = true;
      yard.castShadow = !ctx.low;
      this.geos.push(yard.geometry);
      this.group.add(yard);
    }
    this.hotMat = new THREE.MeshBasicMaterial({ vertexColors: true, color: COAL.clone() });
    const hotMesh = hot.build(this.hotMat, 'forge-hot');
    this.mats.push(this.hotMat);
    if (hotMesh) {
      this.geos.push(hotMesh.geometry);
      this.group.add(hotMesh);
    }

    // The marks: a ring and a ring of runes under a marked tower (a small pool, hidden when unused).
    const outerGeo = new THREE.RingGeometry(0.86, 1, 48).rotateX(-Math.PI / 2);
    const innerGeo = new THREE.RingGeometry(0.62, 0.74, 12, 1, 0, Math.PI * 2).rotateX(-Math.PI / 2);
    this.geos.push(outerGeo, innerGeo);
    for (let i = 0; i < POOL; i++) {
      const mat = new THREE.MeshBasicMaterial({ color: RED.clone(), transparent: true, opacity: 0.6, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
      const runes = new THREE.MeshBasicMaterial({ color: RED_LIGHT.clone(), transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4, wireframe: true });
      this.mats.push(mat, runes);
      const outer = new THREE.Mesh(outerGeo, mat);
      const inner = new THREE.Mesh(innerGeo, runes);
      for (const m of [outer, inner]) {
        m.renderOrder = 2;
        m.frustumCulled = false;
        m.visible = false;
        this.group.add(m);
      }
      this.rings.push({ outer, inner, mat, runes });
    }
    this.group.add(this.glow.mesh);
    for (const gg of [box, slab, rock, cyl]) gg.dispose();
    this.group.updateMatrixWorld(true);
  }

  /** The forge itself, on the node: plinth, hearth, hood and chimney, anvil, bellows, quench trough, tool rack. */
  private buildForge(kit: Kit, hot: Kit, box: THREE.BoxGeometry, cyl: THREE.CylinderGeometry, g0: number): void {
    // A stepped plinth of dark stone.
    kit.add(new THREE.CylinderGeometry(0.88, 0.92, 0.1, 6), WALL_DARK, place(0, g0 + 0.05, 0, Math.PI / 6));
    kit.add(new THREE.CylinderGeometry(0.78, 0.8, 0.08, 6), WALL, place(0, g0 + 0.13, 0, Math.PI / 6));
    const b0 = g0 + 0.17;
    // The hearth: a brick block, its mouth facing the camera (+z), a coal bed glowing on top and in the mouth.
    const hz = -0.12;
    kit.add(box, BRICK_DARK, place(0, b0 + 0.27, hz, 0, new THREE.Vector3(1.0, 0.54, 0.72)));
    // Courses of brick on its face and sides.
    for (let row = 0; row < 4; row++)
      for (let k = 0; k < 5; k++) {
        const x = -0.4 + k * 0.2 + (row % 2) * 0.1;
        if (x > 0.45) continue;
        if (row < 3 && Math.abs(x) < 0.2) continue;
        kit.add(box, BRICK[(row * 5 + k) % BRICK.length] as string, place(x, b0 + 0.07 + row * 0.13, hz + 0.365, 0, new THREE.Vector3(0.18, 0.11, 0.02)));
      }
    for (const sx of [-1, 1]) for (let row = 0; row < 4; row++) for (let k = 0; k < 3; k++) kit.add(box, BRICK[(row + k + (sx > 0 ? 1 : 0)) % BRICK.length] as string, place(sx * 0.505, b0 + 0.07 + row * 0.13, hz - 0.22 + k * 0.22 + (row % 2) * 0.06, 0, new THREE.Vector3(0.02, 0.11, 0.2)));
    // The mouth: a black arch with the coals inside.
    kit.add(box, '#070505', place(0, b0 + 0.2, hz + 0.36, 0, new THREE.Vector3(0.36, 0.3, 0.02)));
    hot.add(box, '#ffffff', place(0, b0 + 0.1, hz + 0.3, 0, new THREE.Vector3(0.32, 0.08, 0.12)));
    for (let k = 0; k < 5; k++) hot.add(new THREE.DodecahedronGeometry(0.045, 0), '#ffffff', place(-0.12 + k * 0.06, b0 + 0.15 + (k % 2) * 0.02, hz + 0.31, k));
    // The coal bed on top, an iron lip round it.
    kit.add(box, IRON, place(0, b0 + 0.55, hz, 0, new THREE.Vector3(0.84, 0.03, 0.58)));
    hot.add(box, '#ffffff', place(0, b0 + 0.565, hz, 0, new THREE.Vector3(0.6, 0.02, 0.4)));
    for (let k = 0; k < 9; k++) hot.add(new THREE.DodecahedronGeometry(0.05, 0), '#ffffff', place(-0.24 + (k % 3) * 0.24 + (k % 2) * 0.04, b0 + 0.59, hz - 0.14 + Math.floor(k / 3) * 0.14, k * 0.7));
    this.mouth.set(0, b0 + 0.6, hz);
    // The hood over it, tapering into the chimney: brick going up, an iron band, a cap.
    kit.add(new THREE.CylinderGeometry(0.26, 0.46, 0.42, 4).rotateY(Math.PI / 4), BRICK[0] as string, place(0, b0 + 0.95, hz - 0.1, 0, new THREE.Vector3(1.3, 1, 1)));
    const cz = hz - 0.12;
    for (let row = 0; row < 5; row++) kit.add(box, BRICK[row % BRICK.length] as string, place(0, b0 + 1.22 + row * 0.15, cz, (row % 2) * 0.02, new THREE.Vector3(0.34, 0.15, 0.32)));
    kit.add(box, IRON, place(0, b0 + 1.45, cz, 0, new THREE.Vector3(0.37, 0.04, 0.35)));
    kit.add(box, WALL_DARK, place(0, b0 + 2.0, cz, 0, new THREE.Vector3(0.42, 0.08, 0.4)));
    hot.add(box, '#ffffff', place(0, b0 + 2.045, cz, 0, new THREE.Vector3(0.24, 0.01, 0.22)));
    this.chimney.set(0, b0 + 2.08, cz);
    // The anvil on its stump, front right; a hammer on its face.
    const ax = 0.62;
    const az = 0.55;
    kit.add(cyl, WOOD, place(ax, b0 + 0.13, az, 0, new THREE.Vector3(0.34, 0.26, 0.34)));
    const am = place(ax, b0 + 0.26, az, 0.5, 0.75);
    kit.add(box, IRON, am, place(0, 0.08, 0, 0, new THREE.Vector3(0.22, 0.16, 0.18)));
    kit.add(box, IRON, am, place(0, 0.21, 0, 0, new THREE.Vector3(0.46, 0.1, 0.2)));
    kit.add(box, IRON_EDGE, am, place(0, 0.265, 0, 0, new THREE.Vector3(0.44, 0.012, 0.18)));
    kit.add(new THREE.ConeGeometry(0.07, 0.24, 6).rotateZ(Math.PI / 2), IRON, am, place(-0.33, 0.22, 0));
    kit.add(box, WOOD, am, place(0.05, 0.3, 0.02, 0.4, new THREE.Vector3(0.36, 0.03, 0.03)));
    kit.add(box, IRON_EDGE, am, place(0.2, 0.31, 0.08, 0.4, new THREE.Vector3(0.06, 0.07, 0.1)));
    // The bellows, left of the hearth: two boards and a leather wedge, its nozzle into the hearth's side.
    const bm = place(-0.72, b0 + 0.18, hz + 0.05, 0.2);
    kit.add(new THREE.CylinderGeometry(0.02, 0.22, 0.6, 4).rotateZ(Math.PI / 2).rotateX(Math.PI / 4), LEATHER, bm, place(0, 0.08, 0, 0, new THREE.Vector3(1, 0.7, 1.4)));
    kit.add(box, PLANK, bm, place(0, 0.2, 0, 0, new THREE.Vector3(0.62, 0.025, 0.36)));
    kit.add(box, PLANK, bm, place(0, -0.04, 0, 0, new THREE.Vector3(0.62, 0.025, 0.36)));
    kit.add(box, WOOD, bm, place(-0.38, 0.2, 0, 0, new THREE.Vector3(0.22, 0.03, 0.05)));
    kit.add(box, IRON, bm, place(0.36, 0.08, 0, 0, new THREE.Vector3(0.16, 0.05, 0.05)));
    // The quench trough, front left: a stone box with dark water.
    const qx = -0.55;
    const qz = 0.62;
    kit.add(box, WALL, place(qx, b0 + 0.13, qz, 0.15, new THREE.Vector3(0.5, 0.26, 0.28)));
    kit.add(box, WATER, place(qx, b0 + 0.265, qz, 0.15, new THREE.Vector3(0.42, 0.01, 0.2)));
    // A rack of tongs and hammers behind, right.
    const rm = place(0.66, b0, -0.45, -0.4);
    for (const sx of [-1, 1]) kit.add(box, WOOD, rm, place(sx * 0.2, 0.32, 0, 0, new THREE.Vector3(0.05, 0.64, 0.05)));
    kit.add(box, WOOD, rm, place(0, 0.6, 0, 0, new THREE.Vector3(0.48, 0.04, 0.05)));
    for (let k = 0; k < 4; k++) {
      kit.add(box, IRON, rm, place(-0.14 + k * 0.09, 0.4, 0.04, 0, new THREE.Vector3(0.012, 0.4, 0.012)));
      kit.add(box, IRON_EDGE, rm, place(-0.14 + k * 0.09, 0.2, 0.04, 0, new THREE.Vector3(k % 2 ? 0.05 : 0.025, 0.05, 0.03)));
    }
    // A coal heap by the bellows, and a sack.
    for (let k = 0; k < 6; k++) kit.add(new THREE.DodecahedronGeometry(0.09, 0), ['#131215', '#1b1a1e'][k % 2] as string, place(-0.82 + (k % 3) * 0.1, b0 + 0.05 + (k > 3 ? 0.07 : 0), -0.42 + Math.floor(k / 3) * 0.1, k));
  }

  /** The fast way coming up: a stone arch at the edge on a heap of rock, two rails on sleepers running toward the forge. */
  private buildRails(kit: Kit, hot: Kit, box: THREE.BoxGeometry, a: number, ER: number): void {
    const ctx = this.ctx;
    const out = ER + 0.35;
    const mx = Math.cos(a) * out;
    const mz = Math.sin(a) * out;
    const my = ctx.ground(mx, mz);
    // The arch faces the forge.
    const yaw = Math.atan2(-mx, -mz);
    const m = place(mx, my, mz, yaw, 1.35);
    kit.add(box, '#050405', m, place(0, 0.48, -0.02, 0, new THREE.Vector3(0.86, 0.95, 0.04)));
    for (let i = 0; i < 9; i++) {
      const t = Math.PI - (i / 8) * Math.PI;
      const ang = new THREE.Matrix4().makeRotationZ(t - Math.PI / 2);
      const pm = place(Math.cos(t) * 0.55, 0.95 + Math.sin(t) * 0.55, 0).multiply(ang);
      kit.add(box, i % 2 ? WALL : WALL_DARK, m, pm.multiply(new THREE.Matrix4().makeScale(0.22, 0.24, 0.3)));
    }
    for (const sx of [-1, 1]) for (let k = 0; k < 4; k++) kit.add(box, k % 2 ? WALL_DARK : WALL, m, place(sx * 0.55, 0.12 + k * 0.24, 0, 0, new THREE.Vector3(0.24, 0.24, 0.32)));
    for (let i = 0; i < 9; i++) {
      const t = (i / 8) * Math.PI;
      kit.add(new THREE.DodecahedronGeometry(0.22 + (i % 3) * 0.06, 0), '#1f1c1d', m, place(Math.cos(t) * 0.95, 0.15 + Math.sin(t) * 0.9, -0.2, i));
    }
    // A lamp in the arch, still lit.
    hot.add(new THREE.CylinderGeometry(0.05, 0.055, 0.15, 6), '#ffffff', m, place(0, 1.23, 0.1));
    this.braziers.push(new THREE.Vector3(0, 1.23, 0.1).applyMatrix4(m));
    this.railMouth.set(mx, my + 0.3, mz);
    // The rails: from inside the mouth in toward the forge's plinth.
    const from = out + 0.2;
    const to = 1.25;
    const len = from - to;
    const steps = Math.ceil(len / 0.3);
    const dx = -Math.cos(a);
    const dz = -Math.sin(a);
    const rail = Math.atan2(dx, dz);
    for (let i = 0; i <= steps; i++) {
      const d = from - (i / steps) * len;
      const x = Math.cos(a) * d;
      const z = Math.sin(a) * d;
      kit.add(box, i % 2 ? WOOD : PLANK, place(x, ctx.ground(x, z) + 0.03, z, rail + Math.PI / 2, new THREE.Vector3(0.62 * 1.35, 0.05, 0.12)));
    }
    for (const side of [-1, 1]) {
      const ox = -dz * side * 0.27;
      const oz = dx * side * 0.27;
      const mid = (from + to) / 2;
      const x = Math.cos(a) * mid + ox;
      const z = Math.sin(a) * mid + oz;
      kit.add(box, IRON_EDGE, place(x, ctx.ground(x, z) + 0.08, z, rail, new THREE.Vector3(0.05, 0.05, len)));
    }
  }

  /** A run of the bastion's wall: a stone curtain with a walk on top and merlons, facing in. */
  private wallRun(kit: Kit, box: THREE.BoxGeometry, p: THREE.Vector3, a: number, len: number, i: number): void {
    const yaw = -a + Math.PI / 2;
    const m = place(p.x + Math.cos(a) * 0.35, p.y, p.z + Math.sin(a) * 0.35, yaw);
    kit.add(box, i % 2 ? WALL : WALL_DARK, m, place(0, 0.55, 0, 0, new THREE.Vector3(len, 1.1, 0.42)));
    kit.add(box, WALL_DARK, m, place(0, 0.08, -0.04, 0, new THREE.Vector3(len + 0.02, 0.16, 0.5)));
    for (let k = 0; k < 3; k++) kit.add(box, WALL, m, place(-len / 3 + k * (len / 3), 1.22, 0.12, 0, new THREE.Vector3(0.2, 0.22, 0.18)));
    // Soot streaks down its face.
    if (i % 3 === 0) kit.add(box, '#262224', m, place(0.1, 0.6, -0.215, 0, new THREE.Vector3(0.12, 0.8, 0.01)));
  }

  /** A brazier on an iron stand by the wall, its fire drawn by the hot mesh and the glows. */
  private brazier(kit: Kit, hot: Kit, box: THREE.BoxGeometry, p: THREE.Vector3, a: number, i: number): void {
    const x = p.x - Math.cos(a) * 0.3;
    const z = p.z - Math.sin(a) * 0.3;
    const y = this.ctx.ground(x, z);
    const m = place(x, y, z, i);
    for (let k = 0; k < 3; k++) {
      const t = (k / 3) * Math.PI * 2;
      kit.add(box, IRON, m, place(Math.cos(t) * 0.1, 0.3, Math.sin(t) * 0.1, -t, new THREE.Vector3(0.03, 0.62, 0.03), 0, Math.cos(t) * 0.15));
    }
    kit.add(new THREE.CylinderGeometry(0.2, 0.12, 0.14, 8), IRON, m, place(0, 0.66, 0));
    hot.add(new THREE.DodecahedronGeometry(0.12, 0), '#ffffff', m, place(0, 0.73, 0, i, new THREE.Vector3(1.3, 0.5, 1.3)));
    this.braziers.push(new THREE.Vector3(0, 0.8, 0).applyMatrix4(m));
  }

  /** Re-dresses the board as the yard: soot and stone, the woods to dark rock, a cold dawn sky, warm forge light. Call once. */
  apply(scene: THREE.Scene, terrain: TerrainBuild, sky: AscuasSky | null, extra: THREE.Object3D[]): void {
    repaint(terrain.mesh.geometry, new THREE.Color('#1a1719'), new THREE.Color('#3f3936'), 0.03, 0.22);
    const c = new THREE.Color();
    const dark = [new THREE.Color('#25232a'), new THREE.Color('#34313a'), new THREE.Color('#2c2a31')];
    const rock = (o: THREE.Object3D): void => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (o.name === 'outskirts-ground') {
        repaint(mesh.geometry, new THREE.Color('#0d0c10'), new THREE.Color('#2a2730'), 0.008, 0.11);
        return;
      }
      if (o.name === 'roads') {
        repaint(mesh.geometry, new THREE.Color('#1b1818'), new THREE.Color('#3a3432'), 0.02, 0.14);
        return;
      }
      if (o.name === 'tufts') {
        o.visible = false;
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      if (mat.emissive && mat.emissive.getHex() !== 0) return;
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && im.instanceColor) {
        for (let i = 0; i < im.count; i++) {
          im.getColorAt(i, c);
          const l = clamp01(lum(c.r, c.g, c.b) / 0.12);
          c.copy(dark[i % 3] as THREE.Color).lerp(dark[1] as THREE.Color, l * 0.5);
          im.setColorAt(i, c);
        }
        im.instanceColor.needsUpdate = true;
        return;
      }
      if (mat.vertexColors || mat.map) return;
      const m2 = mat.clone();
      m2.color.lerp(dark[0] as THREE.Color, 0.7);
      mesh.material = m2;
      this.mats.push(m2);
    };
    terrain.decor.traverse(rock);
    for (const o of extra) o.traverse(rock);
    // Dawn: a cold violet-blue sky over the walls, the forge's warmth on the ground.
    scene.background = new THREE.Color('#1c1a26');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#1e1b26');
    terrain.fog?.tint({ low: '#2a2230', mid: '#1e1b26', far: '#14121a', alpha: 1 });
    if (sky) {
      sky.hemi.color.set('#b0a8c8');
      sky.hemi.groundColor.set('#2a2026');
      sky.hemi.intensity *= 0.85;
      sky.key.color.set('#e8dcd8');
      sky.key.intensity *= 0.75;
      sky.rim.color.set('#ff8a4a');
      sky.rim.intensity *= 0.7;
    }
  }

  /** Every fresh tick: the forge's sparks and smoke, the marks' events, Dorn's automatons coming up the rails. */
  tick(s: BattleState): void {
    const low = this.ctx.low;
    if (s.tick % (low ? 4 : 2) === 0) this.ctx.vfx.burst(this.mouth, { n: low ? 1 : 2, color: s.tick % 4 ? '#ffb070' : '#ff6a2a', speed: 0.01, up: 0.05, size: 0.045, life: 22, gravity: -0.002, spread: 0.25 });
    if (s.tick % (low ? 10 : 5) === 0) this.ctx.vfx.burst(this.chimney, { n: 1, color: '#2a2628', speed: 0.004, up: 0.025, size: 0.45, life: 46, smoke: true, spread: 0.12 });
    if (s.tick % (low ? 14 : 7) === 0) {
      const b = this.braziers[(s.tick >> 3) % Math.max(1, this.braziers.length)];
      if (b) this.ctx.vfx.burst(b, { n: 1, color: '#ff8a4a', speed: 0.008, up: 0.04, size: 0.035, life: 18, gravity: -0.002, spread: 0.08 });
    }
    // The boss's weak side drips embers.
    const w = this.weakAt(s, this.v);
    if (w && s.tick % (low ? 4 : 2) === 0) this.ctx.vfx.burst(w, { n: 1, color: '#ff7a2a', speed: 0.01, up: 0.0, size: 0.04, life: 14, gravity: 0.006, spread: 0.06 });
    for (const e of s._events ?? []) {
      if (e.t !== 'rising') continue;
      const tw = s.structures.find((x) => x.id === e.id);
      const p = this.ctx.at(e.x, e.y, 0.08);
      if (e.kind === 'mark' && tw) {
        // The mark falls: a red ring snaps onto the ground, runes flare.
        this.ctx.vfx.ring(p, 1.8, 0.95, '#ff4f3a', 12, 0.8);
        this.ctx.flare(p.clone().setY(p.y + 1.2), '#ff8a6e', 1.8, 10);
      } else if (e.kind === 'saved') {
        // Kept: a cold flash, the runes blown away.
        this.ctx.vfx.ring(p, 0.4, 1.6, '#e8f4ff', 10, 0.75);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.4), { n: low ? 5 : 12, color: '#cfe8ff', speed: 0.05, up: 0.04, size: 0.05, life: 14, gravity: 0.004, spread: 0.6 });
        this.ctx.flare(p.clone().setY(p.y + 1), '#e8f4ff', 1.6, 8);
      } else if (e.kind === 'turned') {
        // Turned: a red flare up its height, sparks and soot.
        this.ctx.flare(p.clone().setY(p.y + 1.1), '#ff4f3a', 1.6, 10);
        this.ctx.vfx.ring(p, 0.3, 1.8, '#ff4f3a', 14, 0.9);
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.9), { n: low ? 6 : 16, color: '#ff8a6e', speed: 0.08, up: 0.06, size: 0.05, life: 14, gravity: 0.01, spread: 0.4 });
        this.ctx.vfx.burst(p.clone().setY(p.y + 0.9), { n: low ? 3 : 7, color: '#26232a', speed: 0.02, up: 0.04, size: 0.4, life: 22, smoke: true, spread: 0.4 });
        this.ctx.shake(0.08);
      } else if (e.kind === 'dorn') {
        // Dorn's automatons up the rails: dust off the mouth, a cold ring where they stand.
        this.ctx.vfx.burst(this.railMouth, { n: low ? 6 : 14, color: '#5a5650', speed: 0.03, up: 0.03, size: 0.35, life: 26, smoke: true, spread: 0.6 });
        this.ctx.vfx.ring(p, 0.3, 1.8, '#7ff0d8', 14, 0.6);
        this.ctx.flare(p.clone().setY(p.y + 0.8), '#7ff0d8', 2.2, 12);
      }
    }
  }

  /** Where the boss's weak seam is drawn (under the right arm, the sim's facing − a quarter turn), or null. */
  private weakAt(s: BattleState, out: THREE.Vector3): THREE.Vector3 | null {
    const boss = s.bossId ? s.enemies.find((e) => e.id === s.bossId && e.hp > 0) : undefined;
    if (!boss) return null;
    const p = this.ctx.pos(boss.id);
    if (!p) return null;
    // The sim's angles: atan2(dy, dx) in 1024 steps, sim (x, y) on world (x, z).
    const a = ((boss.facing - 256) / 1024) * Math.PI * 2;
    const big = boss.kind === 'hulk';
    const rad = big ? 0.55 : 0.5;
    return out.set(p.x + Math.cos(a) * rad, p.y + (big ? 1.1 : 0.95), p.z + Math.sin(a) * rad);
  }

  /** Every redraw: the forge breathing, the marked towers' rings, the boss's seam, the glows. `hurt`: the forge was just struck. */
  draw(s: BattleState, hurt: boolean): void {
    const dt = Math.min(0.1, Math.max(0, this.now - this.last));
    this.last = this.now;
    this.hit = hurt ? 1 : Math.max(0, this.hit - dt * 3);
    const breathe = 0.82 + 0.12 * Math.sin(this.now * 1.7) + 0.06 * Math.sin(this.now * 5.3);
    this.hotMat.color.copy(COAL).lerp(COAL_HOT, clamp01(0.25 + 0.3 * Math.sin(this.now * 1.7) + this.hit * 0.6)).multiplyScalar(breathe);

    // The marks: each marked tower's ring pulses faster as its time runs out.
    const marks = s.rising?.marks ?? [];
    const span = Math.max(1, this.r.mark);
    for (let i = 0; i < this.rings.length; i++) {
      const ring = this.rings[i] as (typeof this.rings)[number];
      const m = marks[i];
      const tw = m ? s.structures.find((x) => x.id === m.id && !x.destroyed) : undefined;
      if (!m || !tw) {
        ring.outer.visible = false;
        ring.inner.visible = false;
        continue;
      }
      const c = hexToWorld(tw.q, tw.r);
      const p = this.ctx.at(c.x, c.y, 0.07);
      const left = clamp01((m.until - s.tick) / span);
      const urgency = 1 - left;
      const pulse = 0.5 + 0.5 * Math.sin(this.now * (5 + urgency * 16));
      const size = 0.95 + 0.25 * left;
      ring.outer.position.copy(p);
      ring.inner.position.copy(p);
      ring.outer.scale.setScalar(size);
      ring.inner.scale.setScalar(size);
      ring.inner.rotation.y = this.now * (0.8 + urgency * 2.5);
      ring.mat.opacity = 0.45 + 0.45 * pulse;
      ring.mat.color.copy(RED).lerp(RED_LIGHT, pulse * urgency);
      ring.runes.opacity = 0.3 + 0.4 * pulse;
      ring.outer.visible = true;
      ring.inner.visible = true;
    }

    this.glow.begin();
    // The forge's mouth and coal bed, the chimney's top, a warm pool on the yard.
    this.glow.add(this.mouth.x, this.mouth.y + 0.05, this.mouth.z, FIRE_CORE, 0.8, 0.55 * breathe + this.hit * 0.3);
    this.glow.add(this.mouth.x, this.mouth.y + 0.1, this.mouth.z, FIRE, 2.4, 0.34 * breathe);
    this.glow.add(this.mouth.x, this.mouth.y - 0.4, this.mouth.z + 0.4, FIRE, 1.4, 0.4 * breathe);
    this.glow.add(this.chimney.x, this.chimney.y, this.chimney.z, COAL, 1.0, 0.3 * breathe);
    this.glow.add(0, this.ctx.ground(0, 0) + 0.1, 0.3, COAL, 5.5, 0.16 * breathe + this.hit * 0.15);
    if (this.hit > 0.01) this.glow.add(this.mouth.x, this.mouth.y + 0.3, this.mouth.z, RED, 1.8, 0.4 * this.hit);
    for (let i = 0; i < this.braziers.length; i++) {
      const p = this.braziers[i] as THREE.Vector3;
      const f = 0.85 + 0.15 * Math.sin(this.now * 7 + i * 2.1);
      this.glow.add(p.x, p.y + 0.04, p.z, FIRE_CORE, 0.32, 0.6 * f);
      this.glow.add(p.x, p.y, p.z, FIRE, 1.2, 0.25 * f);
      this.glow.add(p.x, this.ctx.ground(p.x, p.z) + 0.05, p.z, FIRE, 1.8, 0.07 * f);
    }
    // The marked towers: a red glow on their tops, stronger as time runs out.
    for (const m of marks) {
      const tw = s.structures.find((x) => x.id === m.id && !x.destroyed);
      if (!tw) continue;
      const c = hexToWorld(tw.q, tw.r);
      const p = this.ctx.at(c.x, c.y, 1.1);
      const urgency = 1 - clamp01((m.until - s.tick) / span);
      this.glow.add(p.x, p.y, p.z, RED, 1.3, 0.25 + 0.35 * urgency * (0.5 + 0.5 * Math.sin(this.now * 14)));
    }
    // The boss's weak seam: molten under the right arm.
    const w = this.weakAt(s, this.v);
    if (w) {
      const f = 0.8 + 0.2 * Math.sin(this.now * 6);
      this.glow.add(w.x, w.y, w.z, SEAM_CORE, 0.3, 0.95 * f);
      this.glow.add(w.x, w.y, w.z, SEAM, 0.8, 0.7 * f);
      this.glow.add(w.x, w.y - 0.15, w.z, RED, 1.3, 0.3 * f);
    }
    this.glow.end();
  }

  frame(now: number): void {
    this.now = now;
  }

  dispose(): void {
    for (const g of this.geos) g.dispose();
    for (const m of this.mats) m.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
